import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "../../../environments/environment";

export interface CapacitacionInvitadoView {
  id: number;
  capacitacion_programacion_id: number;
  actividad_id: number | null;
  propietario_id: number;
  nombre: string;
  asistio: number;
  fecha_asistencia: string | null;
  observaciones: string | null;
  estado: number;
  fechaCreacion: string;
  fechaModificacion: string | null;
}

export interface CapacitacionInvitadoDTO {
  capacitacion_programacion_id?: number;
  actividad_id?: number | null;
  propietario_id?: number;
  nombre?: string;
  asistio?: number;
  fecha_asistencia?: string | null;
  observaciones?: string | null;
  estado?: number;
}

/**
 * 🔹 Payload para confirmar asistencia desde el link público de invitación.
 * A diferencia de CapacitacionInvitadoDTO, aquí la actividad es obligatoria,
 * ya que el backend valida la ventana horaria de esa actividad puntual antes
 * de guardar/actualizar el registro.
 */
export interface ConfirmarAsistenciaPublicaPayload {
  capacitacion_programacion_id: number;
  actividad_id: number;
  propietario_id: number;
  nombre: string;
  observaciones?: string;
}

@Injectable({
  providedIn: "root",
})
export class CapacitacionInvitadosService {
  private apiUrl = `${environment.apiUrl}/api/capacitacion-invitados`;

  constructor(private http: HttpClient) {}

  getAllCapacitacionInvitados(): Observable<CapacitacionInvitadoView[]> {
    return this.http
      .get<CapacitacionInvitadoView[]>(this.apiUrl, { withCredentials: true })
      .pipe(catchError((error) => throwError(() => error)));
  }

  getAllCapacitacionInvitadosActivos(): Observable<CapacitacionInvitadoView[]> {
    return this.http
      .get<CapacitacionInvitadoView[]>(
        `${this.apiUrl}/activos`,
        { withCredentials: true }
      )
      .pipe(catchError((error) => throwError(() => error)));
  }

  getCapacitacionInvitadoById(
    id: string | number,
  ): Observable<CapacitacionInvitadoView[]> {
    return this.http
      .get<CapacitacionInvitadoView[]>(`${this.apiUrl}/${id}`, {
        withCredentials: true,
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  createCapacitacionInvitado(
    payload: CapacitacionInvitadoDTO,
  ): Observable<any> {
    return this.http
      .post<any>(this.apiUrl, payload, {
        withCredentials: true,
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  updateCapacitacionInvitado(
    id: string | number,
    payload: CapacitacionInvitadoDTO,
  ): Observable<any> {
    return this.http
      .put<any>(`${this.apiUrl}/${id}`, payload, {
        withCredentials: true,
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  deleteCapacitacionInvitado(id: string | number): Observable<any> {
    return this.http
      .delete<any>(`${this.apiUrl}/${id}`, {
        withCredentials: true,
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  activar(id: string | number): Observable<any> {
    return this.http
      .put<any>(
        `${this.apiUrl}/${id}/activar`,
        {},
        {
          withCredentials: true,
        },
      )
      .pipe(catchError((error) => throwError(() => error)));
  }

  inactivar(id: string | number): Observable<any> {
    return this.http
      .put<any>(
        `${this.apiUrl}/${id}/inactivar`,
        {},
        {
          withCredentials: true,
        },
      )
      .pipe(catchError((error) => throwError(() => error)));
  }

  // ── PÚBLICO (link de invitación, sin sesión) ────────────────────────

  /**
   * 🔹 Confirma la asistencia de un invitado a una actividad puntual desde
   * el link público. No usa withCredentials (no hay sesión de usuario logueado);
   * el tenant se resuelve por query param, igual que el resto de endpoints
   * "…Publica" del módulo de invitación.
   */
  confirmarAsistenciaPublica(
    payload: ConfirmarAsistenciaPublicaPayload,
    tenantId: number,
  ): Observable<any> {
    const params = new HttpParams().set("tenantId", String(tenantId));

    return this.http
      .post<any>(`${this.apiUrl}/confirmar-asistencia/publica/${tenantId}`, payload, {
        params,
      })
      .pipe(catchError((error) => throwError(() => error)));
  }
}